import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Search, ArrowRight, Sparkles, Sunrise, Sun, Sunset, Moon, Quote } from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORY_META, CATEGORY_IMAGES, HERO_IMAGE } from "@/lib/data";
import { BusinessCard } from "@/components/BusinessCard";
import { useFavorites } from "@/hooks/useFavorites";
import { Button } from "@/components/ui/button";

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return { text: "Good morning", Icon: Sunrise };
  if (h < 17) return { text: "Good afternoon", Icon: Sun };
  if (h < 21) return { text: "Good evening", Icon: Sunset };
  return { text: "Good night", Icon: Moon };
}

function Section({ title, subtitle, items, favs, toggleFav }) {
  if (!items?.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h2 className="font-display text-3xl font-semibold tracking-tight">{title}</h2>
          {subtitle && <p className="text-slate-500 mt-1">{subtitle}</p>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.slice(0, 4).map((b, i) => (
          <BusinessCard key={b.id} biz={b} index={i} favorited={favs.includes(b.id)} onToggleFav={toggleFav} />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [cats, setCats] = useState([]);
  const [events, setEvents] = useState([]);
  const [q, setQ] = useState("");
  const [daily, setDaily] = useState(null);
  const { favs, toggleFav } = useFavorites();
  const greeting = getGreeting();

  useEffect(() => {
    api.get("/businesses").then((r) => setAll(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/daily").then((r) => {
      const list = r.data || [];
      if (list.length) {
        const dayIndex = Math.floor(Date.now() / 86400000) % list.length;
        setDaily(list[dayIndex]);
      }
    });
  }, []);

  const search = (e) => {
    e.preventDefault();
    navigate(`/explore?q=${encodeURIComponent(q)}`);
  };

  const trending = all.filter((b) => b.trending);
  const gems = all.filter((b) => b.hidden_gem);
  const withDeals = all.filter((b) => b.deals_count > 0);
  const newest = [...all].sort((a, b) => (b.created_at > a.created_at ? 1 : -1));

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="Calgary" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/55 to-slate-900/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FDFBF7] via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-24 md:pt-28 md:pb-32">
          <div className="max-w-2xl">
            <div data-testid="hero-greeting" className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md text-white text-sm font-medium px-4 py-1.5 rounded-full mb-6 border border-white/25 animate-fade-up">
              <greeting.Icon className="w-4 h-4 text-orange-300" />
              {greeting.text}, Calgary
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-tighter text-white leading-[1.02]">
              Discover Local.<br />
              <span className="text-orange-400">Live Local.</span>
            </h1>
            <p className="text-lg text-white/85 mt-5 max-w-lg">
              Find the small businesses, hidden gems, events and experiences that make your city worth exploring.
            </p>
            <form onSubmit={search} className="mt-8 flex items-center bg-white rounded-full shadow-2xl p-2 max-w-xl">
              <Search className="w-5 h-5 text-slate-400 ml-4" />
              <input
                data-testid="hero-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="What are you looking for?"
                className="flex-1 bg-transparent border-none focus:ring-0 text-base px-3 outline-none"
              />
              <Button type="submit" data-testid="hero-search-btn" className="rounded-full bg-orange-500 hover:bg-orange-600 px-6">
                Search
              </Button>
            </form>

            {/* Daily encouragement */}
            {daily && (
              <div data-testid="daily-encouragement" className="mt-6 flex items-start gap-3 bg-slate-900/75 backdrop-blur-md border border-white/15 shadow-lg rounded-2xl px-5 py-4 max-w-xl">
                <span className="text-2xl leading-none">{daily.emoji}</span>
                <p className="text-white text-[15px] font-medium leading-relaxed italic">{daily.text}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-14">
        <h2 className="font-display text-3xl font-semibold tracking-tight mb-6">Browse by category</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {cats.map((c) => {
            const meta = CATEGORY_META[c.name] || {};
            const Icon = Icons[meta.icon] || Icons.Sparkles;
            return (
              <button
                key={c.id}
                data-testid={`category-${c.slug}`}
                onClick={() => navigate(`/explore?category=${encodeURIComponent(c.name)}`)}
                className="group relative h-32 rounded-3xl overflow-hidden text-left"
              >
                <img src={CATEGORY_IMAGES[c.name]} alt={c.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className={`absolute inset-0 bg-gradient-to-t ${meta.color || "from-slate-900/80 to-slate-900/30"}`} />
                <div className="relative h-full p-4 flex flex-col justify-between">
                  <Icon className="w-6 h-6 text-white" strokeWidth={1.5} />
                  <span className="text-white font-semibold">{c.name}</span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <Section title="Trending Today" subtitle="What Calgary is loving right now" items={trending} favs={favs} toggleFav={toggleFav} />
      <Section title="Businesses Near You" subtitle="Local spots around the city" items={all} favs={favs} toggleFav={toggleFav} />

      {/* Weekend Events */}
      {events.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-display text-3xl font-semibold tracking-tight">Local Events</h2>
              <p className="text-slate-500 mt-1">Happening soon near you</p>
            </div>
            <Button variant="ghost" onClick={() => navigate("/events")} className="rounded-full">
              See all <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.slice(0, 3).map((e, i) => (
              <div key={e.id} data-testid={`home-event-${e.id}`} onClick={() => navigate("/events")}
                className="cursor-pointer bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:-translate-y-1 transition-all animate-fade-up" style={{ animationDelay: `${i * 60}ms`, opacity: 0 }}>
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={e.image || e.business?.logo} alt={e.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-sm text-orange-600 font-medium">
                    {e.price === 0 ? "Free" : `$${e.price}`} · {e.date}
                  </div>
                  <h3 className="font-display text-lg font-semibold mt-1">{e.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{e.location}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Section title="Today's Deals" subtitle="Save at local businesses" items={withDeals} favs={favs} toggleFav={toggleFav} />
      <Section title="Hidden Gems" subtitle="Off-the-radar local favourites" items={gems} favs={favs} toggleFav={toggleFav} />
      <Section title="Newly Added" subtitle="Fresh on Voyes" items={newest} favs={favs} toggleFav={toggleFav} />

      {/* Business CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="bg-slate-900 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
          <div className="relative">
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-white tracking-tight">Own a local business?</h2>
            <p className="text-slate-300 mt-4 max-w-xl mx-auto">List your business on Voyes, post deals and events, and reach customers exploring your city.</p>
            <Button onClick={() => navigate("/dashboard")} data-testid="cta-list-business" className="mt-7 rounded-full bg-orange-500 hover:bg-orange-600 px-8 py-6 text-base">
              List your business
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
